import { TestResult, ArchetypeScores, Option } from "../types";

const VALID_ARCHETYPES = [
  "jaguar",
  "condor",
  "llama",
  "quetzal",
  "colibri",
  "coqui",
  "buho",
];

export function validateTestResult(result: TestResult): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!result) {
    return { isValid: false, errors: ["Test result is required"] };
  }

  // Validate primary archetype
  if (
    !result.primaryArchetype?.id ||
    !VALID_ARCHETYPES.includes(result.primaryArchetype.id)
  ) {
    errors.push("Invalid primary archetype");
  }

  // Validate secondary archetype
  if (
    !result.secondaryArchetype?.id ||
    !VALID_ARCHETYPES.includes(result.secondaryArchetype.id)
  ) {
    errors.push("Invalid secondary archetype");
  }

  // Validate scores exist and are within bounds
  if (!result.scores || typeof result.scores !== "object") {
    errors.push("Invalid scores object");
  } else {
    Object.entries(result.scores).forEach(([archetype, score]) => {
      if (!VALID_ARCHETYPES.includes(archetype)) {
        errors.push(`Invalid archetype: ${archetype}`);
      }
      if (typeof score !== "number" || score < 0 || score > 100) {
        errors.push(`Invalid score for ${archetype}: ${score}`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function sanitizeTestData(scores: ArchetypeScores): ArchetypeScores {
  const sanitized = { ...scores };

  // Ensure all scores are numbers and within bounds
  Object.keys(sanitized).forEach((key) => {
    const archetypeKey = key as keyof ArchetypeScores;
    let score = sanitized[archetypeKey];

    // Convert to number if needed
    if (typeof score !== "number") {
      score = Number(score);
    }

    // Ensure within bounds
    score = Math.max(0, Math.min(score, 200));

    // Round to 2 decimal places
    sanitized[archetypeKey] = Math.round(score * 100) / 100;
  });

  return sanitized;
}

export function validateAnswers(answers: Option[]): {
  isValid: boolean;
  error?: string;
} {
  if (!Array.isArray(answers)) {
    return { isValid: false, error: "Invalid answers format" };
  }

  if (answers.length === 0) {
    return { isValid: false, error: "No answers provided" };
  }

  for (let i = 0; i < answers.length; i++) {
    const answer = answers[i];

    if (!answer || typeof answer !== "object") {
      return { isValid: false, error: `Invalid answer at position ${i + 1}` };
    }

    if (!answer.scores || typeof answer.scores !== "object") {
      return { isValid: false, error: `Missing scores for answer ${i + 1}` };
    }

    // Validate scores
    for (const [archetype, score] of Object.entries(answer.scores)) {
      if (!VALID_ARCHETYPES.includes(archetype)) {
        return {
          isValid: false,
          error: `Invalid archetype in answer ${i + 1}`,
        };
      }
      if (typeof score !== "number" || score < 0 || score > 5) {
        return {
          isValid: false,
          error: `Invalid score for ${archetype} in answer ${i + 1}`,
        };
      }
    }
  }

  return { isValid: true };
}
